import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, CheckCircle2, MessageCircle, Phone, Mail } from "lucide-react";
import { COMPANY, buildWhatsAppLink } from "@/lib/constants";

export default function Contact() {
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    telefono: "",
    mensaje: "",
  });
  const [status, setStatus] = useState("idle");

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = `👋 ¡Hola Royal Eco! Soy ${form.nombre}.\n\n📝 ${form.mensaje}\n\n📧 Mi email: ${form.email}${
      form.telefono ? `\n📱 Mi teléfono: ${form.telefono}` : ""
    }\n\n♻️ ¡Gracias!`;
    const url = buildWhatsAppLink(msg);
    // Abrimos WhatsApp en una pestaña nueva (la web queda abierta en la actual).
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setStatus("success");
    setTimeout(() => {
      setForm({ nombre: "", email: "", telefono: "", mensaje: "" });
      setStatus("idle");
    }, 3500);
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-asphalt text-bone overflow-hidden py-24 md:py-32"
    >
      <div className="absolute inset-0 z-0">
        <img
          src="/assets/ops2.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-asphalt via-asphalt/95 to-asphalt/80" />
      </div>

      <div className="max-w-[1500px] mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 relative z-10">
        <div className="lg:col-span-5">
          <div className="tag text-lime-400 mb-5 lime-rule">
            Contacto
          </div>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl leading-[0.92]">
            Cotizá tu
            <br />
            <span className="text-lime-400">operación</span> hoy.
          </h2>
          <p className="mt-7 text-bone/75 text-lg max-w-md">
            Compartinos tu caso. Te respondemos con una propuesta técnica y
            comercial transparente, ajustada a tu volumen y tipo de residuo.
          </p>

          <div className="mt-10 space-y-px border-t border-bone/15">
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-info-whatsapp"
              className="flex items-center gap-4 py-5 border-b border-bone/15 hover:bg-bone/5 px-2 group transition-colors"
            >
              <span className="w-10 h-10 inline-flex items-center justify-center bg-whatsapp text-white shrink-0">
                <MessageCircle className="w-5 h-5" />
              </span>
              <div className="flex-1">
                <div className="tag text-bone/60">WHATSAPP</div>
                <div className="font-display text-2xl group-hover:text-lime-400 transition-colors">
                  {COMPANY.whatsappDisplay}
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-bone/40 group-hover:text-lime-400 group-hover:translate-x-1 transition-all" />
            </a>
            <a
              href={`tel:${COMPANY.officeTel}`}
              data-testid="contact-info-office"
              className="flex items-center gap-4 py-5 border-b border-bone/15 hover:bg-bone/5 px-2 group transition-colors"
            >
              <span className="w-10 h-10 inline-flex items-center justify-center bg-bone text-asphalt shrink-0">
                <Phone className="w-5 h-5" />
              </span>
              <div className="flex-1">
                <div className="tag text-bone/60">OFICINA</div>
                <div className="font-display text-2xl group-hover:text-lime-400 transition-colors">
                  {COMPANY.officeDisplay}
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-bone/40 group-hover:text-lime-400 group-hover:translate-x-1 transition-all" />
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              data-testid="contact-info-email"
              className="flex items-center gap-4 py-5 border-b border-bone/15 hover:bg-bone/5 px-2 group transition-colors"
            >
              <span className="w-10 h-10 inline-flex items-center justify-center bg-lime-400 text-asphalt shrink-0">
                <Mail className="w-5 h-5" />
              </span>
              <div className="flex-1">
                <div className="tag text-bone/60">EMAIL PRINCIPAL</div>
                <div className="font-display text-xl group-hover:text-lime-400 transition-colors">
                  {COMPANY.email}
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-bone/40 group-hover:text-lime-400 group-hover:translate-x-1 transition-all" />
            </a>
            <a
              href={`mailto:${COMPANY.emailSecondary}`}
              data-testid="contact-info-email-secondary"
              className="flex items-center gap-4 py-5 hover:bg-bone/5 px-2 group transition-colors"
            >
              <span className="w-10 h-10 inline-flex items-center justify-center bg-lime-400 text-asphalt shrink-0">
                <Mail className="w-5 h-5" />
              </span>
              <div className="flex-1">
                <div className="tag text-bone/60">EMAIL ADMINISTRACIÓN</div>
                <div className="font-display text-xl group-hover:text-lime-400 transition-colors">
                  {COMPANY.emailSecondary}
                </div>
              </div>
              <ArrowRight className="w-5 h-5 text-bone/40 group-hover:text-lime-400 group-hover:translate-x-1 transition-all" />
            </a>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 border-2 border-bone/15 bg-graphite/40 backdrop-blur-sm p-8 md:p-10"
        >
          <div className="flex items-center justify-between mb-8 pb-5 border-b border-bone/15">
            <div className="tag text-lime-400">Formulario de consulta</div>
            <div className="tag text-bone/40">Folio #{Date.now().toString().slice(-6)}</div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="nombre" className="tag text-bone/70 mb-2 block">
                Nombre *
              </label>
              <input
                id="nombre"
                name="nombre"
                value={form.nombre}
                onChange={handleChange}
                required
                placeholder="Nombre completo"
                data-testid="contact-input-nombre"
                className="field"
              />
            </div>
            <div>
              <label htmlFor="telefono" className="tag text-bone/70 mb-2 block">
                Teléfono
              </label>
              <input
                id="telefono"
                name="telefono"
                value={form.telefono}
                onChange={handleChange}
                placeholder="+54 11 ..."
                data-testid="contact-input-telefono"
                className="field"
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="email" className="tag text-bone/70 mb-2 block">
              Email *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              placeholder="contacto@empresa.com"
              data-testid="contact-input-email"
              className="field"
            />
          </div>

          <div className="mb-7">
            <label htmlFor="mensaje" className="tag text-bone/70 mb-2 block">
              Mensaje *
            </label>
            <textarea
              id="mensaje"
              name="mensaje"
              value={form.mensaje}
              onChange={handleChange}
              required
              rows={5}
              placeholder="Tipo de residuo, volumen estimado, ubicación..."
              data-testid="contact-input-mensaje"
              className="field resize-none"
            />
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="contact-submit-button"
              className="inline-flex items-center gap-3 bg-lime-400 hover:bg-lime-500 text-asphalt px-8 py-4 text-[12px] uppercase tracking-widest2 font-semibold transition-colors disabled:opacity-60"
            >
              {status === "loading" ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Enviando…</>
              ) : status === "success" ? (
                <><CheckCircle2 className="w-4 h-4" /> Mensaje enviado</>
              ) : (
                <>Enviar y abrir WhatsApp <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
            <span className="text-xs text-bone/60">
              Al enviar, te llevamos a WhatsApp con tu consulta pre-cargada.
            </span>
          </div>

          {status === "success" && (
            <p role="status" data-testid="contact-success" className="mt-4 text-sm text-lime-400">
              ¡Recibimos tu consulta! Se abrió WhatsApp para continuar la conversación.
            </p>
          )}
        </motion.form>
      </div>
    </section>
  );
}
